/* ============================================================
   Lemoni PDP — Controller (Hero / Buy Box + Mix-and-Match)
   - Bundle-Tier-State, Größe (global, optional), Mix-and-Match-Farbverteilung,
     Varianten-Image-Swap, Galerie, Mobile-Sticky, Countdown.
   - Add-to-cart läuft über Dawns <product-form> / product-form.js -> /cart/add.
     Mehrere Stücke werden als items[] in EINEM Request gelegt -> ein AddToCart-
     Event, Meta-/CAPI-/wetracked-Tracking (admin-seitige Web-Pixels) + Upcart bleiben intakt.
   - Bundle-Rabatt (z. B. 2 kaufen + 1 gratis) macht AIOD serverseitig im Cart, NICHT hier.
   ============================================================ */
(function () {
  'use strict';

  class LemoniPDP {
    constructor(root) {
      this.root = root;
      const dataEl = root.querySelector('[data-adk-data]');
      this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [], colorIndex: 0, sizeIndex: 1 };
      this.variants = this.data.variants || [];
      this.colorIndex = this.data.colorIndex;
      this.sizeIndex = this.data.sizeIndex;

      // Haupt-Buy-Box-Form (Quelle der Wahrheit für den Cart)
      this.buybox = root.querySelector('.adk-buybox');
      this.idInput = this.buybox ? this.buybox.querySelector('.adk-variant-id') : null;
      this.qtyInput = this.buybox ? this.buybox.querySelector('.adk-qty') : null;
      this.itemsContainer = this.buybox ? this.buybox.querySelector('[data-adk-cart-items]') : null;
      this.buyboxCta = this.buybox ? this.buybox.querySelector('.adk-cta') : null;

      // WICHTIG (Upcart): upcart-bundle.js liest beim Add-to-cart die Produkt-ID aus
      // [name=id] der Form. Wird das Feld deaktiviert, bricht Upcart ab
      // ("Unable to get product ID from form. Add to cart failed.") → nichts landet im Cart.
      // Deshalb bleibt [name=id] AKTIV und wird in sync() auf die erste gewählte Variante
      // gespiegelt. Die eigentliche (ggf. mehrfarbige) Bestellung läuft über items[];
      // bei vorhandenem items[] verarbeitet /cart/add die Bulk-Items und ignoriert das
      // einzelne id. quantity bleibt aus (Mengen stecken in items[]).
      if (this.qtyInput) this.qtyInput.disabled = true;

      // Gating-CTAs (Buy Box) — werden bei ungültiger Auswahl deaktiviert.
      // Der Sticky ist NICHT dabei: er bleibt immer aktiv und wechselt seine Funktion
      // (gültige Auswahl → Add-to-cart, sonst → hochscrollen zur Buy Box).
      this.ctas = Array.from(root.querySelectorAll('.adk-cta:not(.adk-sticky__cta)'));
      // Liquid sperrt den CTA nur, wenn das Produkt ausverkauft ist — dann bleibt er gesperrt.
      this.soldOut = !!(this.buyboxCta && this.buyboxCta.disabled);

      // Konfigurator
      this.sizeSelect = root.querySelector('[data-adk-size]');
      this.colorSingleWrap = root.querySelector('[data-adk-colorsingle]');
      this.colorDistWrap = root.querySelector('[data-adk-colordist]');
      this.singleInputs = Array.from(root.querySelectorAll('.adk-swatch48__input'));
      this.colorLabel = root.querySelector('[data-adk-color-label]');
      this.steppers = Array.from(root.querySelectorAll('[data-adk-stepper]'));
      this.distCountEl = root.querySelector('[data-adk-dist-count]');
      this.distTargetEl = root.querySelector('[data-adk-dist-target]');
      this.distCounts = {};

      // Galerie & Sticky
      this.gallery = root.querySelector('[data-adk-gallery]');
      this.sticky = root.querySelector('[data-adk-sticky]');
      this.stickySubmit = root.querySelector('[data-adk-sticky-submit]');
      this.stickyLabel = root.querySelector('[data-adk-sticky-label]');
      this.stickyArrow = root.querySelector('[data-adk-sticky-arrow]');
      this.stickyMode = 'choose';
      this.reviewsMore = root.querySelector('[data-adk-reviews-more]');

      this.bindBundles();
      this.bindMixMatch();
      this.bindGallery();
      this.bindSticky();
      this.bindReviewsMore();
      this.bindReviewTeaser();
      this.bindAddToCart();
      this.onTierChange();
    }

    /* ---------- Tier-State ---------- */
    bindBundles() {
      this.bundleInputs = Array.from(this.root.querySelectorAll('.adk-tier__input'));
      this.bundleInputs.forEach((input) => input.addEventListener('change', () => this.onTierChange()));
    }

    get activeTier() {
      const checked = this.bundleInputs.find((i) => i.checked) || this.bundleInputs[0];
      const paid = parseInt((checked && checked.dataset.paid) || '1', 10);
      const total = parseInt((checked && checked.dataset.total) || '', 10);
      return {
        paid: paid,
        total: total > 0 ? total : paid * 2, // Stück gesamt inkl. Gratis (z. B. 2+1=3); AIOD rabattiert im Cart
        price: (checked && checked.dataset.price) || '',
        compare: (checked && checked.dataset.compare) || '',
      };
    }

    get mode() {
      // Immer Verteil-Modus (Stepper) für ALLE Tiers — der Einzel-Farbpicker wurde entfernt.
      // Auch beim kleinsten Tier verteilt der Kunde die total Stücke über die Farb-Stepper.
      return 'dist';
    }

    onTierChange() {
      const tier = this.activeTier;
      const single = this.mode === 'single';

      if (this.colorSingleWrap) this.colorSingleWrap.hidden = !single;
      if (this.colorDistWrap) this.colorDistWrap.hidden = single;

      // Verteilung bei Tier-Wechsel zurücksetzen (Design: Stepper starten bei 0)
      this.distCounts = {};
      this.steppers.forEach((st) => {
        const c = st.querySelector('[data-adk-count]');
        if (c) c.textContent = '0';
      });
      if (this.distTargetEl) this.distTargetEl.textContent = String(tier.total);
      this.colorHintActive = false; // neuer Tier = neuer Anlauf, Hinweis zurücksetzen

      this.updateDistUI();
      this.sync();
    }

    /* ---------- Mix-and-Match ---------- */
    bindMixMatch() {
      // Einzel-Farbwahl (Tier 1)
      this.singleInputs.forEach((input) =>
        input.addEventListener('change', () => {
          if (this.colorLabel) this.colorLabel.textContent = input.dataset.value;
          this.swapGalleryToColor(input.dataset.value);
          this.sync();
        })
      );

      // Größe (global, nur wenn das Produkt eine Größen-Option hat)
      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', () => this.sync());
      }

      // Stepper (Verteil-Modus)
      this.steppers.forEach((st) => {
        const color = st.dataset.value;
        const countEl = st.querySelector('[data-adk-count]');
        st.querySelectorAll('[data-adk-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = parseInt(btn.dataset.adkStep, 10);
            this.stepColor(color, delta, countEl);
          });
        });
      });
    }

    sumCounts() {
      return Object.keys(this.distCounts).reduce((a, k) => a + (this.distCounts[k] || 0), 0);
    }

    stepColor(color, delta, countEl) {
      const target = this.activeTier.total;
      const cur = this.distCounts[color] || 0;
      const sum = this.sumCounts();

      if (delta > 0 && sum >= target) return; // Plus gesperrt, Ziel erreicht
      let next = cur + delta;
      if (next < 0) next = 0;
      // Summe darf das Ziel nicht überschreiten
      if (delta > 0 && sum - cur + next > target) next = target - (sum - cur);

      this.distCounts[color] = next;
      if (countEl) countEl.textContent = String(next);
      if (next > 0) this.swapGalleryToColor(color);

      this.updateDistUI();
      this.sync();
    }

    updateDistUI() {
      const target = this.activeTier.total;
      const sum = this.sumCounts();
      if (this.distCountEl) this.distCountEl.textContent = String(sum);
      this.updateColorHint();

      // Plus-Buttons sperren, sobald Ziel erreicht; Minus bei 0 sperren
      this.steppers.forEach((st) => {
        const color = st.dataset.value;
        const cur = this.distCounts[color] || 0;
        const minus = st.querySelector('[data-adk-step="-1"]');
        const plus = st.querySelector('[data-adk-step="1"]');
        if (minus) minus.disabled = cur <= 0;
        if (plus) plus.disabled = sum >= target;
      });
    }

    findVariant(color, size) {
      return this.variants.find((v) => {
        const opts = v.options || [];
        const matchColor = this.colorIndex == null || this.colorIndex < 0 || opts[this.colorIndex] === color;
        const matchSize = this.sizeIndex == null || this.sizeIndex < 0 || opts[this.sizeIndex] === size;
        return matchColor && matchSize;
      });
    }

    /* ---------- Cart-Items ableiten ---------- */
    resolveItems() {
      const size = this.sizeSelect ? this.sizeSelect.value : null;
      const tier = this.activeTier;

      if (this.mode === 'single') {
        const checked = this.singleInputs.find((i) => i.checked) || this.singleInputs[0];
        const color = checked ? checked.dataset.value : null;
        const variant = this.findVariant(color, size);
        if (!variant || !variant.available) return { valid: false, items: [] };
        return { valid: true, items: [{ id: variant.id, qty: tier.total }] };
      }

      // Verteil-Modus: Summe muss EXAKT dem Tier-Ziel entsprechen
      const sum = this.sumCounts();
      if (sum !== tier.total) return { valid: false, items: [] };

      const items = [];
      let ok = true;
      Object.keys(this.distCounts).forEach((color) => {
        const qty = this.distCounts[color];
        if (qty > 0) {
          const variant = this.findVariant(color, size);
          if (!variant || !variant.available) { ok = false; return; }
          items.push({ id: variant.id, qty: qty });
        }
      });
      if (!ok || !items.length) return { valid: false, items: [] };
      return { valid: true, items: items };
    }

    setCartItems(items) {
      if (!this.itemsContainer) return;
      this.itemsContainer.innerHTML = '';
      items.forEach((it, i) => {
        const idIn = document.createElement('input');
        idIn.type = 'hidden';
        idIn.name = 'items[' + i + '][id]';
        idIn.value = String(it.id);
        const qIn = document.createElement('input');
        qIn.type = 'hidden';
        qIn.name = 'items[' + i + '][quantity]';
        qIn.value = String(it.qty);
        this.itemsContainer.appendChild(idIn);
        this.itemsContainer.appendChild(qIn);
      });
    }

    /* ---------- Sync ---------- */
    sync() {
      const resolved = this.resolveItems();
      const valid = resolved.valid;

      this.setCartItems(valid ? resolved.items : []);

      // [name=id] auf die erste gewählte Variante spiegeln, damit Upcart eine gültige
      // Produkt-ID aus der Form lesen kann (sonst bricht upcart-bundle.js den Add ab).
      if (this.idInput && valid && resolved.items.length) {
        this.idInput.value = String(resolved.items[0].id);
      }

      // CTA bleibt klickbar (sieht aktiv aus). Bei unvollständiger Farbauswahl zeigt der
      // Klick einen Hinweis (promptColors) statt still nichts zu tun. Nur ausverkauft = gesperrt.
      this.ctas.forEach((cta) => {
        if (this.soldOut) return;
        cta.disabled = false;
        cta.setAttribute('aria-disabled', 'false');
        cta.toggleAttribute('data-incomplete', !valid);
      });

      // Sticky bleibt immer aktiv, wechselt aber Funktion + Beschriftung:
      // gültige Auswahl → Add-to-cart (→), sonst → hochscrollen zur Buy Box (↑).
      if (this.stickySubmit) {
        this.stickyMode = valid ? 'add' : 'choose';
        if (this.stickyLabel) {
          this.stickyLabel.textContent = valid
            ? (this.stickySubmit.dataset.labelAdd || 'In den Warenkorb')
            : (this.stickySubmit.dataset.labelChoose || 'Bundle wählen');
        }
        if (this.stickyArrow) this.stickyArrow.textContent = valid ? '→' : '↑';
      }
    }

    /* ---------- Reviews: „Weitere anzeigen" ---------- */
    bindReviewsMore() {
      if (!this.reviewsMore) return;
      const hidden = Array.from(this.root.querySelectorAll('.adk-review--more'));
      if (!hidden.length) { this.reviewsMore.hidden = true; return; }
      this.reviewsMore.addEventListener('click', () => {
        hidden.forEach((card) => card.removeAttribute('hidden'));
        this.reviewsMore.hidden = true;
      });
    }

    /* ---------- Reviews-Teaser: Karussell-Dots + Auto-Center ---------- */
    bindReviewTeaser() {
      const track = this.root.querySelector('[data-adk-revteaser-track]');
      if (!track) return;
      const cards = track.children;
      if (!cards.length) return;
      const dots = Array.from(this.root.querySelectorAll('[data-adk-revteaser-dots] .adk-revteaser__dot'));

      const setActive = (idx) => {
        dots.forEach((d, i) => {
          if (i === idx) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      };

      // Aktive Karte = die, deren Mitte der Viewport-Mitte am nächsten ist (wie Design renderVals)
      const onScroll = () => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let best = 0, bestDist = Infinity;
        for (let i = 0; i < cards.length; i++) {
          const c = cards[i];
          const cc = c.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(cc - center);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        setActive(best);
      };

      // Beim Laden: zentriert auf Karte 2 (Design: componentDidMount → children[1])
      const centerSecond = () => {
        const card = cards[1] || cards[0];
        track.scrollLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
        onScroll();
      };

      track.addEventListener('scroll', onScroll, { passive: true });
      if ('requestAnimationFrame' in window) {
        requestAnimationFrame(centerSecond);
      } else {
        centerSecond();
      }
    }

    /* ---------- Add-to-cart (Bundle, mehrfarbig) ----------
       Upcart kapert den nativen Form-Submit und legt nur die einzelne [name=id]-
       Variante an (items[] wird ignoriert). Daher unterbinden wir den Submit per
       preventDefault auf den CTA-Klick und führen den mehrfarbigen Add selbst über
       den nativen /cart/add.js mit items[] aus — EIN Request = EIN AddToCart-Event.
       Upcart hört auf /cart/add und öffnet/aktualisiert seinen Drawer von selbst. */
    bindAddToCart() {
      if (!this.buyboxCta) return;
      this.buyboxCta.addEventListener('click', (e) => {
        e.preventDefault(); // stoppt den Form-Submit (und damit Upcarts Einzel-Add)
        if (this.buyboxCta.getAttribute('aria-disabled') === 'true') return;
        if (this.buyboxCta.hasAttribute('data-incomplete')) { this.promptColors(); return; }
        this.addBundleToCart();
      });
    }

    addBundleToCart() {
      if (this.adding) return;
      const resolved = this.resolveItems();
      if (!resolved.valid || !resolved.items.length) return;

      this.adding = true;
      this.buyboxCta.classList.add('loading');
      const spinner = this.buyboxCta.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: resolved.items.map((i) => ({ id: i.id, quantity: i.qty })) }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.status && data.status !== 200) {
            console.error('Lemoni /cart/add abgelehnt:', data.description || data.message);
            return;
          }
          // Upcart Public API (docs.aftersell.com): Cart neu laden + Drawer öffnen.
          // Setzt voraus, dass in Upcart "Enhanced Ajax API Compatibility" aktiv ist,
          // damit Upcart unseren nativen items[]-Add nicht überschreibt.
          if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
          if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
          // Fallback für andere Cart-Drawer / No-Upcart-Kontext:
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((err) => { console.error('Lemoni add-to-cart fehlgeschlagen', err); })
        .finally(() => {
          this.adding = false;
          this.buyboxCta.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    /* ---------- Farbauswahl unvollständig: hinscrollen, markieren, sagen was fehlt ---------- */
    promptColors() {
      const wrap = this.colorDistWrap;
      if (!wrap || wrap.hidden) { this.scrollToBuyBox(); return; }
      this.colorHintActive = true;
      this.updateColorHint();
      wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      wrap.classList.remove('adk-needs');
      void wrap.offsetWidth; // Animation neu starten
      wrap.classList.add('adk-needs');
    }

    updateColorHint() {
      const wrap = this.colorDistWrap;
      if (!wrap) return;
      if (!this.colorHint) {
        this.colorHint = document.createElement('p');
        this.colorHint.className = 'adk-colorhint';
        this.colorHint.setAttribute('role', 'alert');
        this.colorHint.hidden = true;
        wrap.appendChild(this.colorHint);
      }
      const left = this.activeTier.total - this.sumCounts();
      if (this.colorHintActive && left > 0) {
        this.colorHint.textContent = 'Bitte wähle noch ' + left + (left === 1 ? ' Farbe' : ' Farben') + ' aus';
        this.colorHint.hidden = false;
      } else {
        this.colorHintActive = false;
        this.colorHint.hidden = true;
        wrap.classList.remove('adk-needs');
      }
    }

    /* ---------- Sanft hoch zur Buy Box + Bundle-Auswahl kurz highlighten ---------- */
    scrollToBuyBox() {
      const target = this.root.querySelector('.adk-bundles') || this.buybox;
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('adk-attn');
      window.setTimeout(() => target.classList.remove('adk-attn'), 1400);
    }

    /* ---------- Mobile Sticky ---------- */
    bindSticky() {
      if (!this.sticky) return;
      if (this.stickySubmit) {
        this.stickySubmit.addEventListener('click', () => {
          if (this.stickyMode === 'add') {
            this.addBundleToCart();
          } else {
            this.promptColors();
          }
        });
      }
      const anchor = this.buyboxCta;
      if (anchor && 'IntersectionObserver' in window) {
        const obs = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            // Sticky NUR zeigen, wenn der Haupt-CTA nach OBEN aus dem Blick gescrollt ist
            // (User ist darunter). Liegt der CTA noch UNTER dem Viewport (z. B. ganz oben
            // beim Laden), bleibt der Sticky aus. boundingClientRect.top < 0 = oberhalb.
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.sticky.classList.toggle('is-visible', show);
            this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          });
        }, { rootMargin: '0px 0px -10% 0px' });
        obs.observe(anchor);
      }
    }

    /* ---------- Galerie ---------- */
    bindGallery() {
      if (!this.gallery) return;
      this.slides = Array.from(this.gallery.querySelectorAll('.adk-gallery__slide'));
      this.dots = Array.from(this.gallery.querySelectorAll('.adk-gallery__dot'));
      this.thumbs = Array.from(this.gallery.querySelectorAll('.adk-gallery__thumb'));
      this.track = this.gallery.querySelector('.adk-gallery__track');

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.setActiveSlide(i)));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.scrollToSlide(i)));

      if (this.track) {
        this.track.addEventListener('scroll', () => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.markActive(i);
        }, { passive: true });
      }
      this.setActiveSlide(0);
    }

    scrollToSlide(i) {
      if (this.track) this.track.scrollTo({ left: i * this.track.clientWidth });
      this.markActive(i);
    }

    setActiveSlide(i) {
      this.slides.forEach((s, idx) => {
        if (idx === i) s.setAttribute('data-active', ''); else s.removeAttribute('data-active');
      });
      this.markActive(i);
    }

    markActive(i) {
      this.activeSlide = i;
      this.dots.forEach((d, idx) => d.setAttribute('aria-current', idx === i ? 'true' : 'false'));
      this.thumbs.forEach((t, idx) => t.setAttribute('aria-current', idx === i ? 'true' : 'false'));
    }

    swapGalleryToColor(colorValue) {
      if (!this.gallery || !this.slides) return;
      const variant = this.variants.find((v) => (v.options || [])[this.colorIndex] === colorValue && v.featured_media_id);
      if (!variant) return;
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media_id));
      if (idx >= 0) {
        this.setActiveSlide(idx);
        this.scrollToSlide(idx);
      }
    }
  }

  /* ---------- FAQ-Akkordeon (maximal 1 Item gleichzeitig offen) ---------- */
  function initFAQAccordion() {
    document.querySelectorAll('[data-adk-section]').forEach((section) => {
      const items = Array.from(section.querySelectorAll('.adk-faq__item'));
      items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (item.open) {
            items.forEach((other) => { if (other !== item) other.open = false; });
          }
        });
      });
    });
  }

  /* ---------- Countdown (Offer-Stat, Final CTA …) ---------- */
  function pad(n) { return String(n).padStart(2, '0'); }

  function initCountdowns() {
    const els = document.querySelectorAll('[data-adk-countdown]');
    if (!els.length) return;

    const tick = () => {
      const now = Date.now();
      els.forEach((el) => {
        const end = Date.parse(el.dataset.end);
        if (isNaN(end)) return;
        let diff = Math.max(0, end - now);
        const day = 86400000, hour = 3600000, min = 60000, sec = 1000;
        const d = Math.floor(diff / day); diff -= d * day;
        const h = Math.floor(diff / hour); diff -= h * hour;
        const m = Math.floor(diff / min); diff -= m * min;
        const sc = Math.floor(diff / sec);
        const fmt = el.dataset.format || 'dhms';
        if (end <= now) {
          el.textContent = el.dataset.expired || 'Angebot endet bald';
          return;
        }
        if (fmt === 'dhm') {
          el.textContent = pad(d) + ' : ' + pad(h) + ' : ' + pad(m);
        } else if (fmt === 'hms') {
          el.textContent = pad(h) + ' : ' + pad(m) + ' : ' + pad(sc);
        } else {
          el.textContent = pad(d) + ' : ' + pad(h) + ' : ' + pad(m) + ' : ' + pad(sc);
        }
      });
    };
    tick();
    if (!window.__adkCountdownTimer) {
      window.__adkCountdownTimer = setInterval(tick, 1000);
    }
  }

  function init() {
    document.querySelectorAll('[data-adk-section]').forEach((el) => {
      if (!el.__adkInit) { el.__adkInit = true; new LemoniPDP(el); }
    });
    initCountdowns();
    initFAQAccordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
